/**
 * helpers/flag.ts — flag colour, icon, and worst-of helpers.
 *
 * Flag rules (from PROJECT_SPEC §6):
 *   GREEN  ≤ 1 day since last submission
 *   AMBER  > 1 day, ≤ 7 days
 *   RED    > 7 days
 *   GREY   never submitted
 *
 * `worstFlag` ignores GREY (no submission ≠ "fine") so a bank that has
 * submitted Loan today but never submitted KYC still surfaces as GREEN
 * for the metrics that DO exist, while the GREY pill on the KYC column
 * makes the gap obvious.
 */

import type { Flag } from '../type/api';

/** Returns CSS class fragment matching `.status-pill.<class>` in app.css. */
export function flagClass(flag: Flag): 'green' | 'amber' | 'red' | 'grey' {
	switch (flag) {
		case 'GREEN':
			return 'green';
		case 'AMBER':
			return 'amber';
		case 'RED':
			return 'red';
		case 'GREY':
		default:
			return 'grey';
	}
}

/** Hex token for chart strips and dot indicators. */
export function flagColorHex(flag: Flag): string {
	switch (flag) {
		case 'GREEN':
			return '#10B981';
		case 'AMBER':
			return '#F59E0B';
		case 'RED':
			return '#E11D48';
		case 'GREY':
		default:
			return '#C4C9D3';
	}
}

/**
 * Glyph used inside the StatusPill in addition to colour, so colour-blind
 * users still get the signal. Keep these single characters so they fit
 * inside the pill even at compact density.
 */
export function flagIcon(flag: Flag): string {
	switch (flag) {
		case 'GREEN':
			return '✓';
		case 'AMBER':
			return '!';
		case 'RED':
			return '✕';
		case 'GREY':
		default:
			return '–';
	}
}

export function flagLabel(flag: Flag): string {
	switch (flag) {
		case 'GREEN':
			return 'On time';
		case 'AMBER':
			return 'Stale';
		case 'RED':
			return 'Overdue';
		case 'GREY':
		default:
			return 'Never';
	}
}

/**
 * Severity ranking — RED highest, then AMBER, GREEN, GREY last (because
 * GREY means "no data" which is a different signal from "fine").
 */
const SEVERITY: Record<Flag, number> = {
	GREY: 0,
	GREEN: 1,
	AMBER: 2,
	RED: 3,
};

/** Pick the worst (most severe) flag in a list. Returns GREY for an empty list. */
export function worstFlag(flags: Flag[]): Flag {
	if (flags.length === 0) return 'GREY';
	return flags.reduce<Flag>((acc, f) => (SEVERITY[f] > SEVERITY[acc] ? f : acc), 'GREY');
}

/** Compute the flag for a given days-since value (NULL ⇒ GREY). */
export function computeFlag(daysSince: number | null | undefined): Flag {
	if (daysSince === null || daysSince === undefined) return 'GREY';
	if (daysSince <= 1) return 'GREEN';
	if (daysSince <= 7) return 'AMBER';
	return 'RED';
}

/**
 * NPA flag rules (PHASE2_SPEC §3): ≤ 7 % green, 7-10 % amber, > 10 % red.
 * NULL or 0 loanCr ⇒ GREY (no signal).
 */
export function npaFlag(npaPct: number | null | undefined): Flag {
	if (npaPct === null || npaPct === undefined || !Number.isFinite(npaPct)) return 'GREY';
	if (npaPct <= 7) return 'GREEN';
	if (npaPct <= 10) return 'AMBER';
	return 'RED';
}

/**
 * Data-quality flag rules (PHASE2_SPEC §3): < 10 % green, 10-25 % amber, > 25 % red.
 * Higher % = more missing values = worse. NULL ⇒ GREY.
 */
export function dqFlag(missingPct: number | null | undefined): Flag {
	if (missingPct === null || missingPct === undefined || !Number.isFinite(missingPct)) return 'GREY';
	if (missingPct < 10) return 'GREEN';
	if (missingPct <= 25) return 'AMBER';
	return 'RED';
}
